import React, { useEffect, useRef, useState, useCallback } from "react";
import { api } from "../lib/api";
import { MessageCircle, Send, Loader2 } from "lucide-react";

export const AdminChatTab = () => {
    const [conversations, setConversations] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [reply, setReply] = useState("");
    const [sending, setSending] = useState(false);
    const prevTotalUnreadRef = useRef(0);
    const endRef = useRef(null);

    const beep = useCallback(() => {
        try {
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            const o = ctx.createOscillator();
            const g = ctx.createGain();
            o.type = "sine";
            o.frequency.value = 880;
            g.gain.value = 0.08;
            o.connect(g); g.connect(ctx.destination);
            o.start(); g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.3);
            o.stop(ctx.currentTime + 0.3);
        } catch {}
    }, []);

    const fetchConversations = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/chat/conversations");
            const totalUnread = data.reduce((s, c) => s + (c.unread_for_admin || 0), 0);
            if (totalUnread > prevTotalUnreadRef.current) {
                beep();
                if (typeof document !== "undefined" && document.title && !document.title.startsWith("🔔")) {
                    document.title = `🔔 ${totalUnread} • ${document.title}`;
                }
            } else if (totalUnread === 0 && typeof document !== "undefined" && document.title.startsWith("🔔")) {
                document.title = document.title.replace(/^🔔 \d+ • /, "");
            }
            prevTotalUnreadRef.current = totalUnread;
            setConversations(data);
        } catch {}
    }, [beep]);

    const fetchMessages = useCallback(async (id) => {
        if (!id) return;
        try {
            const { data } = await api.get(`/admin/chat/${id}/messages`);
            setMessages(data.messages);
        } catch {}
    }, []);

    useEffect(() => {
        fetchConversations();
        const t = setInterval(fetchConversations, 4000);
        return () => clearInterval(t);
    }, [fetchConversations]);

    useEffect(() => {
        if (!activeId) return;
        fetchMessages(activeId);
        const t = setInterval(() => fetchMessages(activeId), 3000);
        return () => clearInterval(t);
    }, [activeId, fetchMessages]);

    useEffect(() => {
        if (endRef.current) endRef.current.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const sendReply = async (e) => {
        e.preventDefault();
        if (!reply.trim() || !activeId) return;
        setSending(true);
        try {
            await api.post(`/admin/chat/${activeId}/reply`, { text: reply.trim() });
            setReply("");
            await fetchMessages(activeId);
            await fetchConversations();
        } finally {
            setSending(false);
        }
    };

    const active = conversations.find((c) => c.id === activeId);

    return (
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4 h-[70vh]" data-testid="admin-chat">
            {/* List */}
            <aside className="border border-white/10 bg-[#16161a] overflow-y-auto">
                <div className="p-3 border-b border-white/10 font-heading uppercase tracking-wider text-xs text-white/60 sticky top-0 bg-[#16161a]">
                    Συνομιλίες ({conversations.length})
                </div>
                {conversations.length === 0 ? (
                    <p className="text-white/40 text-sm text-center py-8 px-4">Καμία συνομιλία ακόμα.</p>
                ) : conversations.map((c) => (
                    <button
                        key={c.id}
                        data-testid={`conv-${c.id}`}
                        onClick={() => setActiveId(c.id)}
                        className={`w-full text-left px-4 py-3 border-b border-white/5 transition ${
                            activeId === c.id ? "bg-brand-red/10 border-l-2 border-l-brand-red" : "hover:bg-white/5"
                        }`}
                    >
                        <div className="flex items-center justify-between gap-2">
                            <span className="font-heading uppercase text-sm truncate">{c.customer_name}</span>
                            {c.unread_for_admin > 0 && (
                                <span className="bg-brand-red text-white text-[10px] font-bold px-2 rounded-full">{c.unread_for_admin}</span>
                            )}
                        </div>
                        {c.customer_phone && <div className="text-xs text-white/50 truncate">{c.customer_phone}</div>}
                        <div className="text-[10px] text-white/40 mt-1">
                            {new Date(c.last_message_at).toLocaleString("el-GR")}
                        </div>
                    </button>
                ))}
            </aside>

            {/* Thread */}
            <section className="border border-white/10 bg-[#0c0c0e] flex flex-col">
                {!active ? (
                    <div className="flex-1 grid place-items-center text-white/40 text-sm">
                        <div className="text-center">
                            <MessageCircle size={32} className="mx-auto mb-3 text-white/20" />
                            Επίλεξε συνομιλία για να δεις μηνύματα.
                        </div>
                    </div>
                ) : (
                    <>
                        <header className="p-4 border-b border-white/10 bg-[#16161a]">
                            <div className="font-heading uppercase">{active.customer_name}</div>
                            {active.customer_phone && <div className="text-xs text-white/50 mt-0.5">{active.customer_phone}</div>}
                        </header>

                        <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="admin-chat-thread">
                            {messages.map((m) => (
                                <div
                                    key={m.id}
                                    className={`max-w-[70%] px-3 py-2 rounded-lg text-sm leading-relaxed break-words ${
                                        m.sender === "admin"
                                            ? "ml-auto bg-brand-red text-white"
                                            : "bg-white/10 text-white"
                                    }`}
                                >
                                    {m.text}
                                    <div className={`text-[10px] mt-1 ${m.sender === "admin" ? "text-white/70" : "text-white/40"}`}>
                                        {new Date(m.created_at).toLocaleTimeString("el-GR", { hour: "2-digit", minute: "2-digit" })}
                                    </div>
                                </div>
                            ))}
                            <div ref={endRef} />
                        </div>

                        <form onSubmit={sendReply} className="p-3 border-t border-white/10 flex gap-2 bg-[#16161a]">
                            <input
                                data-testid="admin-reply-input"
                                value={reply}
                                onChange={(e) => setReply(e.target.value)}
                                placeholder="Απάντηση..."
                                className="flex-1 bg-[#0c0c0e] border border-white/15 px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-brand-red"
                            />
                            <button type="submit" data-testid="admin-reply-send" disabled={sending || !reply.trim()} className="h-9 px-4 bg-brand-red hover:bg-brand-redhover grid place-items-center rounded-sm disabled:opacity-50">
                                {sending ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
                            </button>
                        </form>
                    </>
                )}
            </section>
        </div>
    );
};
